import { call, delay, fork, getContext, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { queryData } from './apiService';
import { AuthError, LoadType, MutationType, LoadOptions, DeregisterUserStatus, EmailVerificationCodeStatus, IConsentType } from './apiService/types';
import {
  FACEBOOK_LOGIN_WITH_CONSENTS,
  GOOGLE_LOGIN_WITH_CONSENTS,
  PASSWORD_LOGIN_WITH_CONSENTS,
  LOGOUT,
  SIGN_IN_SIGN_UP_WITH_EMAIL,
  UPDATE_USER,
  USER_LOGIN_SOCIAL,
  USER_LOGIN_WITH_EMAIL_AND_PASSWORD,
  SET_AUTH_MODAL,
} from 'store/state/app/types';
import {
  loginFacebookWithConsents,
  loginGoogleWithConsents,
  loginWithPasswordAndConsents,
  logout,
  setAuthModal,
  setCommutePopupStatus,
  setToastMessage,
  signInSignUpWithEmail,
  updateUser,
  userLoginWithEmailAndPassword,
  toggleLoginSuccessModal,
  userLoginSocial,
  setGALoadTimestamp,
  setInvestorsLoginStep,
} from 'store/state/app/actions';
import { fetchMore, resetDomainData } from 'store/state/domainData/actions';
import { MUTATE, SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import { RootAction } from 'store/state';
import { mutate, resetMutationResponse, setMutationResponse } from 'store/state/mutationsResponse/actions';
import { AuthenticationModalType, AuthSource, UserAlreadyExists } from 'components/authentication/types';
import { GoogleAPI } from 'utils/gapi';
import { AppleAPI } from 'utils/aapi';
import config from 'config';
import { CommutePopupStatus } from 'components/commute-popup/types';
import { ProfileTab } from 'components/profile/Profile';
import { navigateTo } from 'store/state/router/actions';
import { Route, State as RouteState } from 'config/routes';
import { routeNameSelector, routeSelector, showAgentConsoleBulletinFormSelector } from 'store/state/selectors/router';
import { RESET, RESET_SSR_HYDRATION, SET_DOMAIN_DATA, SET_FETCH_MORE_RESULT } from 'store/state/domainData/types';
import { isConnectedUserSelector, isUserSelector, isVisitorUserSelector, userProfileSelector, userAuthErrorSelector, userHasAcceptedInvestorsTermsSelector } from 'store/state/domainData/selectors';
import { LoginResponseNG, ResetPasswordStatus, RegisterType } from 'utils/entities';
import { ApolloError } from 'apollo-client';
import { UserTokenStore } from 'helpers/userToken';
import { TRANSITION_SUCCESS, TRANSITION_START } from '../state/router/types';
import { mutateWorker } from './apiService/mutationsWatcher';
import { isServer } from 'utils';
import { isBot } from 'utils/bot';
import { authModalAuthSourceVariablesSelector, authModalStatusSelector, investorsLoginSourceVariablesSelector } from 'store/state/app/selectors';
import { RouterAction } from 'store/state/router/actionType';
import { waitForUserResolve } from './routing/handlers/utils';
import { getQAAutomationParams } from 'utils/qa';
import { SignInResponse } from 'typings/window';
import { signInSuccessMutationPattern, signUpSuccessMutationPattern } from 'store/sagas/analytics/userAuthEvents';
import { uploadBulletinDraft } from 'components/bulletin-form/wizard/helpers';
import { TrafficSegmentationRules, trafficSegmentationShowSelector, TrafficSegmentationPlace } from 'store/state/selectors/traffic';
import { userLoginPattern } from './fetchFavoritesWatcher';
import { loadCurrentPromotionPlan } from './privateUserMonetizationWatcher';
import { investorsLobbyMaybeRedirectToHomePage } from './routing/handlers/investors';
import { getProExpertUrl } from 'helpers/proExpert';


function* registerVisitor(
  key: string = null
) {
  // defensive checks:
  const isUser: boolean = yield select(isUserSelector);
  const ssr: boolean = yield call(isServer);
  const isSEOMode: boolean = yield getContext('isSEOMode');
  const bot = isSEOMode || (!ssr && isBot());
  const canRegister = !(bot || ssr || isUser);

  if (canRegister) {
    yield call(mutateWorker, mutate({
      mutationType: MutationType.RegisterVisitor,
      meta: { variables: {}, key },
    }));
  }
  else {
    yield put(resetDomainData({ loadType: LoadType.CurrentUser }));
  }
}

interface IError {
  errorMessage: string;
}

const isImplementsError = (value: unknown): value is IError =>
  typeof value === 'object' && value !== null && 'errorMessage' in value;

interface AuthErrorMeta {
  reason: AuthError;
  message: string;
  __typename: LoginResponseNG['__typename'];
}

const maybeConvertToAuthErrorMeta = (response: LoginResponseNG): AuthErrorMeta | null => {
  if (response.__typename === 'RequireInvestorsTermsApprovalError') {
    return {
      reason: null,
      message: null,
      __typename: response.__typename,
    };
  }
  else if (isImplementsError(response)) {
    return {
      reason: response.errorMessage as AuthError,
      message: response.errorMessage,
      __typename: response.__typename,
    };
  }
  return null;
};

function maybeConvertToError(action: ReturnType<typeof setMutationResponse>): AuthErrorMeta {
  try {
    if (action.mutationType === MutationType.GoogleLogin) {
      return maybeConvertToAuthErrorMeta(action.meta.response.data.googleLogin);
    }
    if (action.mutationType === MutationType.SignInSignUpWithEmail) {
      return maybeConvertToAuthErrorMeta(action.meta.response.data.loginV3);
    }
    if (action.mutationType === MutationType.TempTokenAuth) {
      return maybeConvertToAuthErrorMeta(action.meta.response.data.completeAutoLogin);
    }
    if (action.mutationType === MutationType.FacebookLogin) {
      return maybeConvertToAuthErrorMeta(action.meta.response.data.facebookLogin);
    }
    if (action.mutationType === MutationType.LoginWithEmailAndPassword) {
      return maybeConvertToAuthErrorMeta(action.meta.response.data.passwordLoginV3);
    }
    return null;
  }
  catch {
    return null;
  }
}

function* userMutationsEffectsWorker(action: ReturnType<typeof setMutationResponse>) {
  const isUser: boolean = yield select(isUserSelector);
  const route: RouteState = yield select(routeSelector);
  const isInvestorsLogin = route.name === Route.InvestorsLoginPage;
  const error = (action.meta.response && action.meta.response.errors && action.meta.response.errors[0]) || maybeConvertToError(action);

  if (error && error.reason === AuthError.InvalidRegistrationType) {
    yield put(setAuthModal({ type: UserAlreadyExists[error.message] }, { isUserInitiated: false }));
    return;
  }

  switch (action.mutationType) {
    case MutationType.DeregisterUser:
      switch (action.meta.response.data.deregisterUser.status) {
        case DeregisterUserStatus.DEREGISTER_USER_FAILURE:
        case DeregisterUserStatus.DEREGISTER_USER_NO_USER:
          yield put(setToastMessage({ term: 'authentication.deregister.fail' }));
          break;
        case DeregisterUserStatus.DEREGISTER_USER_SUCCESS:
          yield put(setToastMessage({ term: 'authentication.deregister.success' }));
          yield put(resetDomainData({ loadType: LoadType.UserContent }));
          yield put(resetDomainData({ loadType: LoadType.CurrentUser }));
          yield put(navigateTo(Route.Home));
          break;
      }
      break;
    case MutationType.GenerateEmailVerificationCode: {
      const res = action.meta.response && action.meta.response.data && action.meta.response.data.generateEmailVerificationCode;
      if (res) {
        switch (res.statusCode) {
          case EmailVerificationCodeStatus.SUCCESS:
            if (isInvestorsLogin) {
              yield put(setInvestorsLoginStep({ type: AuthenticationModalType.UserEmailVerification }, { isUserInitiated: false }));
            }
            else {
              yield put(setAuthModal({ type: AuthenticationModalType.UserEmailVerification }, { isUserInitiated: false }));
            }
            return;
          case EmailVerificationCodeStatus.BLOCKED:
            yield put(setAuthModal({ type: AuthenticationModalType.Block, authSourceVariables: { verificationTTL: res.remainedTtl } }, { isUserInitiated: false }));
            return;
        }
      }

      return;
    }
    case MutationType.TempTokenAuth:
      if (error) {
        yield put(resetDomainData({ loadType: LoadType.CurrentUser }));
        yield put(setToastMessage({ term: 'authentication.authError', params: { error: error.reason } }));
      }
      break;
    case MutationType.SignInSignUpWithEmail: {
      const isConnectedUser = yield select(isConnectedUserSelector);
      if (isConnectedUser) {
        yield put(setAuthModal({ type: null }, { isUserInitiated: false }));
      }
      else if (error) {
        switch (error.reason) {
          case AuthError.ConfirmConsentsV2:
            yield put(setAuthModal({ type: AuthenticationModalType.Consents }, { isUserInitiated: false }));
            return;
          case AuthError.NeedPassword:
            if (isInvestorsLogin) {
              yield put(setInvestorsLoginStep({ type: AuthenticationModalType.EnterPassword }, { isUserInitiated: false }));
            }
            else {
              yield put(setAuthModal({ type: AuthenticationModalType.EnterPassword }, { isUserInitiated: false }));
            }
            return;
          case AuthError.NeedPasswordReset:
          case AuthError.NeedEmailVerificationError:
            yield put(mutate({
              mutationType: MutationType.GenerateEmailVerificationCode,
              meta: {
                variables: {
                  email: action.meta.variables.email,
                },
              },
            }));
            return;
          case AuthError.LoginFailed:
            return;
          case AuthError.InvalidAgentRegistration:
            yield put(setAuthModal({ type: AuthenticationModalType.AgentCompanyNotFound }, { isUserInitiated: false }));
            return;
          case AuthError.DeletedUser:
            yield put(setToastMessage({ term: 'authentication.authError.deleted.user' }));
            return;
        }
        yield put(setToastMessage({ term: 'authentication.authError', params: { error: error.reason } }));
      }
      break;
    }
    case MutationType.CompleteAppleLogin: {
      if (action.meta.response && action.meta.response.data && action.meta.response.data.completeAppleLogin && action.meta.response.data.completeAppleLogin.__typename) {
        switch (action.meta.response.data.completeAppleLogin.__typename) {
          case 'LoginOutcome': {
            yield put(setAuthModal({ type: null }, { isUserInitiated: false }));
            yield put(toggleLoginSuccessModal(true));
            break;
          }
          case 'UserAlreadyExistsError': {
            yield put(setAuthModal({ type: AuthenticationModalType.DuplicatedEmail }, { isUserInitiated: false }));
            break;
          }
          case 'LoginFailedError': {
            yield put(setAuthModal({ type: null }, { isUserInitiated: false }));
            yield put(setToastMessage({ term: 'authentication.authError', params: { error: 'login-failed' } }));
            break;
          }
        }
      }
      break;
    }
    case MutationType.AppleLogin: {
      if (action.meta.response && action.meta.response.data && action.meta.response.data.appleLogin && action.meta.response.data.appleLogin.__typename) {
        switch (action.meta.response.data.appleLogin.__typename) {
          case 'LoginOutcome': {
            yield put(setAuthModal({ type: null }, { isUserInitiated: false }));
            yield put(toggleLoginSuccessModal(true));
            break;
          }
          case 'RequireTermsApprovalError': {
            yield put(setAuthModal({ type: AuthenticationModalType.AfterSocialConsents, authSourceVariables: { email: action.meta.response.data.appleLogin.associatedEmail, appleCode: action.meta.response.data.appleLogin.loginVerificationToken } }, { isUserInitiated: false }));
            break;
          }
          case 'UserAlreadyExistsError': {
            yield put(setAuthModal({ type: AuthenticationModalType.DuplicatedEmail }, { isUserInitiated: false }));
            break;
          }
          case 'LoginFailedError': {
            yield put(setAuthModal({ type: null }, { isUserInitiated: false }));
            yield put(setToastMessage({ term: 'authentication.authError', params: { error: 'login-failed' } }));
            break;
          }
        }
      }
      break;
    }
    case MutationType.FacebookLogin:
    case MutationType.GoogleLogin:
    case MutationType.LoginWithEmailAndPassword: {
      const isConfirmConsentsAuthError = (reason: AuthError) => reason === AuthError.ConfirmConsentsV2;
      if (isUser && !error) {
        if (isInvestorsLogin) {
          yield put(setInvestorsLoginStep({ type: null, authSource: null, authSourceVariables: null }, { isUserInitiated: false }));
          yield call(investorsLobbyMaybeRedirectToHomePage);
        }
        else {
          yield put(setAuthModal({ type: null, authSource: null, authSourceVariables: null }, { isUserInitiated: false }));
          yield put(toggleLoginSuccessModal(true));
          if (route.params.noLoginRedirect) {
            break;
          }
        }
      }
      else if (action.mutationType === MutationType.GoogleLogin && error && isConfirmConsentsAuthError(error.reason)) {
        const loginStepAction = isInvestorsLogin ? setInvestorsLoginStep : setAuthModal;
        yield put(loginStepAction({
          type: AuthenticationModalType.Consents,
          authSource: AuthSource.Google,
          authSourceVariables: { idToken: action.meta.variables.idToken, googleClientId: action.meta.variables.clientId },
        }, { isUserInitiated: false }
        ));
      }
      else if (action.mutationType === MutationType.FacebookLogin && error && isConfirmConsentsAuthError(error.reason)) {
        const { email, accessToken, fbUserId } = action.meta.variables;
        const loginStepAction = isInvestorsLogin ? setInvestorsLoginStep : setAuthModal;
        yield put(loginStepAction({
          type: AuthenticationModalType.Consents,
          authSource: AuthSource.Facebook,
          authSourceVariables: { email, accessToken, fbUserId },
        }, { isUserInitiated: false }
        ));
      }
      else if (action.mutationType === MutationType.LoginWithEmailAndPassword && error && isConfirmConsentsAuthError(error.reason)) {
        const { email, password } = action.meta.variables;
        const loginStepAction = isInvestorsLogin ? setInvestorsLoginStep : setAuthModal;
        yield put(loginStepAction({
          type: AuthenticationModalType.Consents,
          authSource: AuthSource.EmailAndPassword,
          authSourceVariables: { email, password },
        }, { isUserInitiated: false }
        ));
      }
      else if (action.mutationType === MutationType.LoginWithEmailAndPassword && error && (error as any).__typename === 'RequireInvestorsTermsApprovalError') {
        const { email, password } = action.meta.variables;
        yield put(setInvestorsLoginStep({
          type: AuthenticationModalType.InvestorsLoginTerms,
          authSource: AuthSource.EmailAndPassword,
          authSourceVariables: { email, password },
        }, { isUserInitiated: true }
        ));
      }
      else if (action.mutationType === MutationType.LoginWithEmailAndPassword && error && error.reason === AuthError.LoginFailed) {
        if (isInvestorsLogin) {
          yield put(setInvestorsLoginStep({ type: AuthenticationModalType.EnterPassword, authSourceVariables: { error: error.reason === AuthError.LoginFailed } }, { isUserInitiated: false }));
        }
        else {
          yield put(setAuthModal({ type: AuthenticationModalType.EnterPassword, authSourceVariables: { error: error.reason === AuthError.LoginFailed } }, { isUserInitiated: false }));
        }
      }
      else if (error && error.reason === AuthError.LoginFailed) {
        yield put(setToastMessage({ term: 'authentication.authError', params: { error: error.reason } }));
      }

      break;
    }

    case MutationType.UpdateUserProfile: {
      if (isUser && action.meta.key && action.meta.key !== 'moveIn') {
        if (action.meta.key === 'commutePopup') {
          yield put(setCommutePopupStatus(CommutePopupStatus.Resolved));
          yield put(setToastMessage({ term: 'profile.saveChangesFeedbackMessage', params: { tab: ProfileTab.Personal } }));
        }
        else {
          yield put(setToastMessage({ term: 'profile.saveChangesFeedbackMessage', params: { tab: action.meta.key } }));
        }
      }
      break;
    }

    case MutationType.ResetPasswordUpdate: {
      if (action.meta.response.data.resetPasswordUpdate && action.meta.response.data.resetPasswordUpdate.status === ResetPasswordStatus.RESET_SUCCESS) {
        yield put(setToastMessage({ term: 'authentication.resetPasswordUpdateFeedbackMessage' }));
        const isConnectedUser = yield select(isConnectedUserSelector);

        if (!isConnectedUser) {
          yield put(mutate({
            mutationType: MutationType.LoginWithEmailAndPassword,
            meta: {
              variables: {
                email: action.meta.variables.email,
                password: action.meta.variables.newPassword,
                consents: action.meta.variables.consents,
              },
            },
          }));
          if (!isInvestorsLogin) {
            yield take(userLoginPattern);
          }
        }

        if (!isInvestorsLogin) {
          yield put(setAuthModal({ type: null }, { isUserInitiated: false }));
        }
      }

      break;
    }

    case MutationType.VerifyEmailVerificationCode: {
      if (action.meta.response.data.verifyEmailVerificationCode.statusCode === EmailVerificationCodeStatus.SUCCESS) {
        yield delay(500);
        if (isInvestorsLogin) {
          yield put(setInvestorsLoginStep({ type: AuthenticationModalType.SetPassword }, { isUserInitiated: false }));
        }
        else {
          yield put(setAuthModal({ type: AuthenticationModalType.SetPassword }, { isUserInitiated: false }));
        }
      }
      if (action.meta.response.data.verifyEmailVerificationCode.statusCode === EmailVerificationCodeStatus.BLOCKED) {
        yield put(setAuthModal({ type: AuthenticationModalType.Block, authSourceVariables: { verificationTTL: action.meta.response.data.verifyEmailVerificationCode.remainedTtl } }, { isUserInitiated: false }));
      }

      break;
    }
  }
}

function* userUpdateWorker(action: ReturnType<typeof updateUser>) {
  const isUser: boolean = yield select(isUserSelector);

  if (isUser) {
    yield put(mutate({
      mutationType: MutationType.UpdateUserProfile,
      meta: action.payload,
    }));
  }
}

function* authModalWorker(googleAPI: GoogleAPI, action: ReturnType<typeof setAuthModal>) {
  try {
    if (action.payload.type) {
      yield call([ googleAPI, googleAPI.cancelPrompt ]);
    }
    else {
      yield call(waitForUserResolve);
      const user = yield select(userProfileSelector);
      if (!user || (user.registrationType === RegisterType.Visitor)) {
        yield call([ googleAPI, googleAPI.reactivateOneTapLogin ]);
      }
    }
  }
  catch {
    const logger = yield getContext('logger');
    logger.log('authModalWorker error with googleAPI');
  }
}

function* loginOrRegisterWorker(
  mutationType: MutationType.LoginWithEmailAndPassword | MutationType.SignInSignUpWithEmail,
  action: ReturnType<typeof signInSignUpWithEmail> | ReturnType<typeof userLoginWithEmailAndPassword>
) {
  const userData = action.payload;

  yield put(mutate({
    meta: {
      variables: {
        ...userData,
      },
    },
    mutationType,
  }));
}

function* socialLoginWorker(appleAPI: AppleAPI, action: ReturnType<typeof userLoginSocial>) {
  const logger = yield getContext('logger');


  switch (action.payload) {
    case 'apple': {
      let data: SignInResponse = null;
      try {
        data = yield yield appleAPI.signIn();
      }
      catch {
        logger.log('apple social login canceled');
      }

      if (data && data.authorization && data.authorization.code) {
        yield put(mutate({
          mutationType: MutationType.AppleLogin,
          meta: {
            variables: {
              code: data.authorization.code,
              redirectUri: config.apple.services.appleSignIn.redirectUri,
            },
          },
        }));

      }
      break;
    }
    default:
      break;
  }
}

const ITEM_PAGE_ROUTES = new Set([
  Route.UnitPage,
  Route.MadadPage,
  Route.MadadArchivePage,
  Route.MadadArchive2020Page,
  Route.MadadSearchPage,
  Route.MadadAgentPage,
  Route.AddressPage,
  Route.ProjectPage,
]);

const INVESTORS_ITEM_PAGE_ROUTES = new Set([
  Route.InvestorsHomePage,
  Route.InvestorsLobbyPage,
  Route.InvestorsNeighbourhoodPage,
  Route.InvestorsWizardPage,
  Route.InvestorsSearchPage,
]);

function* loadProfessionalUserInfo() {
  yield call(waitForUserResolve);
  const isUser: boolean = yield select(isConnectedUserSelector);

  if (isUser) {
    const logger = yield getContext('logger');
    try {
      const loadOptions: LoadOptions<LoadType.GetProfessionalUserInfo> = {
        loadType: LoadType.GetProfessionalUserInfo,
        meta: { variables: {} },
      };
      yield call(queryData, loadOptions);
    }
    catch (e) {
      const errorText = `Failed to load professional user info. Error: ${e.message}`;
      logger.error(errorText);
      TrackJS.track(errorText);
    }
  }
}

function* logoutWorker() {
  yield put(resetDomainData({ loadType: LoadType.CurrentUser }));
  yield put(resetDomainData({ loadType: LoadType.GetAgentOrOfficeSummary }));
  yield put(resetDomainData({ loadType: LoadType.GetDealsAffiliationRequests }));
  yield put(resetDomainData({ loadType: LoadType.GetProfessionalUserInfo }));

  if (uploadBulletinDraft.get()) uploadBulletinDraft.clear();

  const route: RouteState = yield select(routeSelector);

  if (!ITEM_PAGE_ROUTES.has(route.name)) {
    if (INVESTORS_ITEM_PAGE_ROUTES.has(route.name)) {
      yield put(setInvestorsLoginStep({ type: null, authSource: null, authSourceVariables: null }, { isUserInitiated: false }));
      yield put(navigateTo(Route.InvestorsLoginPage, {}));
    }
    else {
      const routeName = route.params.dealType && route.params.term ? Route.Search : Route.Home;
      yield put(navigateTo(routeName, route.params));
    }
  }
}

function* resetWatcher() {
  const userTokenStore: UserTokenStore = yield getContext('tokenStore');
  userTokenStore.remove();
  yield call(registerVisitor);
}

function* resetVerificationEmailWatcher() {
  yield put(resetMutationResponse({ mutationType: MutationType.VerifyEmailVerificationCode, key: null }));
  yield put(resetMutationResponse({ mutationType: MutationType.GenerateEmailVerificationCode, key: null }));
}

export enum TokenError {
  Expired = 'token-expired',
  Invalid = 'token-invalid',
}

export function isTokenInvalidError(e: Error) {
  return (e instanceof ApolloError) && (e.graphQLErrors.some(error => error.message === TokenError.Expired || error.message === TokenError.Invalid));
}

export function* fetchUser() {
  const userTokenStore: UserTokenStore = yield getContext('tokenStore');
  const route: RouteState = yield select(routeSelector);

  try {
    if (userTokenStore.get() && !route.params.loginToken) {
      const loadOpts: LoadOptions<LoadType.CurrentUser> = {
        loadType: LoadType.CurrentUser,
        meta: {
          variables: {},
          errorPolicy: 'none',
        },
      };
      yield call(queryData, loadOpts);
    }
    else if (route.params.loginToken) {
      yield call(mutateWorker, mutate({
        mutationType: MutationType.TempTokenAuth,
        meta: {
          variables: {
            loginToken: route.params.loginToken,
          },
        },
      }));
      yield put(navigateTo(route.name, { ...route.params, loginToken: undefined }, { ssrHttpStatus: 302, replace: true }));
    }
    else {
      yield call(registerVisitor);
    }
    yield call(loadCurrentPromotionPlan);
  }
  catch (e) {
    if (isTokenInvalidError(e)) {
      yield call(registerVisitor);
    }
  }
}

function* resetAuthOnRouteChangeWorker(action: RouterAction) {
  const authModal = yield select(authModalStatusSelector);
  const hasRouteChanged = action.type === TRANSITION_START // defensive check
    && action.payload.previousRoute
    && action.payload.route.name !== action.payload.previousRoute.name;

  if (authModal !== null && hasRouteChanged) {
    yield put(setAuthModal({ type: null }, { isUserInitiated: false }));
  }
}


const userTokenMutations: Set<MutationType> = new Set([
  MutationType.FacebookLogin,
  MutationType.GoogleLogin,
  MutationType.LoginWithEmailAndPassword,
  MutationType.SignInSignUpWithEmail,
  MutationType.TempTokenAuth,
  MutationType.RegisterVisitor,
  MutationType.UpdateUserProfile,
  MutationType.ResetPasswordUpdate,
  MutationType.DeregisterUser,
  MutationType.AppleLogin,
  MutationType.CompleteAppleLogin,
  MutationType.VerifyEmailVerificationCode,
  MutationType.GenerateEmailVerificationCode,
]);

const checkUserMutationsResponse = (action: RootAction) => action.type === SET_MUTATION_RESPONSE && userTokenMutations.has(action.mutationType);

const resetUserDataPattern = (action: RootAction) => (
  action.type === RESET && action.payload.loadType === LoadType.CurrentUser
);

const proExpertAuthPattern = (action: RootAction) => action.type === TRANSITION_SUCCESS && Boolean(action.payload.route.params.proExpertAuth);

export const checkUserUpdatedDataPattern = (action: RootAction) => (action.type === SET_FETCH_MORE_RESULT && action.loadType === LoadType.CurrentUser);
export const checkUserSearchHistoryUpdatedPattern = (action: RootAction) => (action.type === SET_DOMAIN_DATA && action.loadType === LoadType.CurrentUserSearchHistory);

export const loginSuccessMutationPattern = (action: RootAction) => (signInSuccessMutationPattern(action) || signUpSuccessMutationPattern(action));

const resetVerificationEmailMutationResponse = (action: RootAction) => action.type === SET_AUTH_MODAL && action.payload.type === AuthenticationModalType.UserEmailVerification;

const routesWithoutOneTap = new Set([
  Route.UploadBulletin,
  Route.EditBulletin,
  Route.EditCommercialBulletin,
]);

export const googleLoginPattern = (action: RootAction) => (
  (action.type === SET_DOMAIN_DATA && action.loadType === LoadType.CurrentUser)
  || (action.type === RESET && action.payload.loadType === LoadType.CurrentUser)
  || (action.type === LOGOUT)
  || (action.type === MUTATE)
  || (action.type === SET_MUTATION_RESPONSE)
  || (action.type === RESET_SSR_HYDRATION && action.payload.loadType === LoadType.CurrentUser)
  || (action.type === SET_AUTH_MODAL && action.payload.type !== null)
  || (action.type === TRANSITION_SUCCESS && routesWithoutOneTap.has(action.payload.route.name))
);

export function* googleAuthWatcher(googleAPI: GoogleAPI, _: ReturnType<typeof logout>) {
  const ssr = yield call(isServer);
  const { googleLogin } = getQAAutomationParams();
  const { [TrafficSegmentationPlace.All]: allowShowPopup }: TrafficSegmentationRules = yield select(trafficSegmentationShowSelector);

  if (!ssr && !isBot() && googleLogin !== 'disable') {
    const logger = yield getContext('logger');

    yield call(waitForUserResolve);
    const isVisitor = yield select(isVisitorUserSelector);
    const userError = yield select(userAuthErrorSelector);
    const route: RouteState = yield select(routeSelector);
    const isAuthModalOpen: AuthenticationModalType = yield select(authModalStatusSelector);

    if ((isVisitor && (routesWithoutOneTap.has(route.name))) || isAuthModalOpen) {
      yield call([ googleAPI, googleAPI.loadClient ]);
      yield put(setGALoadTimestamp(Date.now()));
      const idToken = yield call([ googleAPI, googleAPI.buttonLogin ]);
      yield put(mutate({
        mutationType: MutationType.GoogleLogin,
        meta: {
          variables: {
            idToken,
          },
        },
      }));
    }

    if (isVisitor || userError) {
      try {
        let idToken: string = null;
        if (allowShowPopup) {
          idToken = yield call([ googleAPI, googleAPI.oneTapLogin ]);
        }
        if (!idToken) {
          idToken = yield call([ googleAPI, googleAPI.buttonLogin ]);
        }

        yield put(mutate({
          mutationType: MutationType.GoogleLogin,
          meta: {
            variables: {
              idToken,
            },
          },
        }));
      }
      catch {
        logger.log('Google login error');
      }
    }
  }
}

function* loginSuccessWorker() {
  const route: RouteState = yield select(routeSelector);
  const isConnectedUser: boolean = yield select(isConnectedUserSelector);

  const isProExpertAuthMode = Boolean(route.params.proExpertAuth);
  if (isProExpertAuthMode && isConnectedUser) {
    // navigate to proExpert page
    yield window.location.href = getProExpertUrl();
  }

  const showAgentConsoleBulletinForm: boolean = yield select(showAgentConsoleBulletinFormSelector);
  if (showAgentConsoleBulletinForm && route.name === Route.UploadBulletin) {
    yield put(navigateTo(Route.UploadBulletinForm, { ...route.params }, { replace: true }));
  }

  if (isConnectedUser) {
    yield call(loadProfessionalUserInfo);
  }
}

function* googleAuthWithConsents(action: ReturnType<typeof loginGoogleWithConsents>) {
  const routeName = yield select(routeNameSelector);
  const isInvestors = routeName === Route.InvestorsLoginPage;
  const { idToken, googleClientId } = yield select(isInvestors ? investorsLoginSourceVariablesSelector : authModalAuthSourceVariablesSelector);
  yield put(mutate({
    mutationType: MutationType.GoogleLogin,
    meta: {
      variables: {
        idToken,
        clientId: googleClientId,
        consents: action.payload,
      },
    },
  }));
}

function* facebookAuthWithConsents(action: ReturnType<typeof loginFacebookWithConsents>) {
  const { fbUserId, email, accessToken } = yield select(authModalAuthSourceVariablesSelector);
  yield put(mutate({
    mutationType: MutationType.FacebookLogin,
    meta: {
      variables: {
        fbUserId,
        email,
        accessToken,
        consents: action.payload,
      },
    },
  }));
}

const consentsAcceptedPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.AcceptConsents && action.meta.response.data.acceptConsents.success
);

function* passwordAuthWithConsents(action: ReturnType<typeof loginWithPasswordAndConsents>) {
  const route: RouteState = yield select(routeSelector);
  const isInvestorsArea = route.name === Route.InvestorsLoginPage;
  const isConnectedUser = yield select(isConnectedUserSelector);
  const investorsTermsAccepted = yield select(userHasAcceptedInvestorsTermsSelector);

  if (isInvestorsArea && isConnectedUser && !investorsTermsAccepted) {
    yield put(mutate({
      mutationType: MutationType.AcceptConsents,
      meta: {
        variables: {
          input: {
            consents: [ IConsentType.INVESTORS_TERMS_AND_CONDITIONS ],
          },
        },
      },
    }));

    yield take(consentsAcceptedPattern);

    const tokenStore: UserTokenStore = yield getContext('tokenStore');
    const userToken = tokenStore.get();

    yield put(fetchMore({ loadType: LoadType.CurrentUser, meta: { variables: { userToken } } }));

    yield take(checkUserUpdatedDataPattern);

    // TODO: check if investors wizard was submitted then redirect to InvestorsHomePage
    yield call(investorsLobbyMaybeRedirectToHomePage);
  }
  else if (isInvestorsArea && !isConnectedUser) {
    const { email, password } = yield select(investorsLoginSourceVariablesSelector);

    yield put(mutate({
      mutationType: MutationType.LoginWithEmailAndPassword,
      meta: {
        variables: {
          email,
          password,
          consents: action.payload,
        },
      },
    }));

    yield take(userLoginPattern);

    const tokenStore: UserTokenStore = yield getContext('tokenStore');
    const userToken = tokenStore.get();

    yield put(fetchMore({ loadType: LoadType.CurrentUser, meta: { variables: { userToken } } }));

    yield take(checkUserUpdatedDataPattern);

    // TODO: check if investors wizard was submitted then redirect to InvestorsHomePage
    yield call(investorsLobbyMaybeRedirectToHomePage);
  }
  else {
    const { email, password } = yield select(authModalAuthSourceVariablesSelector);

    yield put(mutate({
      mutationType: MutationType.LoginWithEmailAndPassword,
      meta: {
        variables: {
          email,
          password,
          consents: action.payload,
        },
      },
    }));
  }
}

function* proExpertAuthWorker() {
  yield call(waitForUserResolve);
  const isConnectedUser: boolean = yield select(isConnectedUserSelector);

  if (isConnectedUser) {
    // make sure we don't show any auth popups for already logged in users
    yield put(navigateTo(Route.Home, {}, { replace: true }));
  }
  else {
    yield put(setAuthModal({ type: AuthenticationModalType.ProExpert }, { isUserInitiated: false }));
  }
}

export function* userWatcher() {
  const googleAPI = new GoogleAPI({
    'client_id': config.google.clientId,
  });

  const appleAPI = new AppleAPI({
    clientId: config.apple.services.appleSignIn.clientId,
    scope: 'name email',
    redirectURI: config.apple.services.appleSignIn.redirectUri,
    usePopup: true,
  });

  yield takeEvery(resetUserDataPattern, resetWatcher);
  yield takeEvery(resetVerificationEmailMutationResponse, resetVerificationEmailWatcher);
  yield takeEvery(checkUserMutationsResponse, userMutationsEffectsWorker);
  yield takeEvery(proExpertAuthPattern, proExpertAuthWorker);

  yield takeEvery(LOGOUT, logoutWorker);
  yield takeEvery(UPDATE_USER, userUpdateWorker);
  yield takeEvery(SET_AUTH_MODAL, authModalWorker, googleAPI);
  yield takeEvery(USER_LOGIN_SOCIAL, socialLoginWorker, appleAPI);
  yield takeEvery(USER_LOGIN_WITH_EMAIL_AND_PASSWORD, loginOrRegisterWorker, MutationType.LoginWithEmailAndPassword);
  yield takeEvery(SIGN_IN_SIGN_UP_WITH_EMAIL, loginOrRegisterWorker, MutationType.SignInSignUpWithEmail);
  yield takeEvery(TRANSITION_START, resetAuthOnRouteChangeWorker);

  yield take(TRANSITION_SUCCESS);
  yield fork(fetchUser);
  yield fork(loadProfessionalUserInfo);

  yield takeEvery(loginSuccessMutationPattern, loginSuccessWorker);
  yield takeLatest(googleLoginPattern, googleAuthWatcher, googleAPI);
  yield takeEvery(GOOGLE_LOGIN_WITH_CONSENTS, googleAuthWithConsents);
  yield takeEvery(FACEBOOK_LOGIN_WITH_CONSENTS, facebookAuthWithConsents);
  yield takeEvery(PASSWORD_LOGIN_WITH_CONSENTS, passwordAuthWithConsents);
}
